import { useEffect, useState, type FormEvent } from 'react';
import type { State } from '../shared/model';
import type { AdsInsightSnapshot, AdsMetric, AdsWorkspaceData } from '../shared/ads';
import './ads.css';

const dayLabel = (value: string) =>
  new Date(`${value}T12:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const valueLabel = (metric: AdsMetric, currency: string) =>
  metric.value === null
    ? 'Indisponível'
    : metric.unit === 'currency'
      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(metric.value)
      : metric.value.toLocaleString('pt-BR');
function Reading({
  snapshot,
  accountName,
}: {
  snapshot: AdsInsightSnapshot;
  accountName?: string;
}) {
  const name = snapshot.accountName || accountName;
  return (
    <article className="ads-reading">
      <div className="ads-reading-heading">
        <strong>
          {snapshot.level === 'account' ? 'Conta de anúncios inteira' : 'Campanha de anúncios'}
        </strong>
        <span>
          {snapshot.currency} · {dayLabel(snapshot.since)} a {dayLabel(snapshot.until)}
        </span>
      </div>
      <p className="ads-reading-account">
        Conta: {name ? `${name} · ` : ''}
        {snapshot.accountId}
      </p>
      <p className="fine-print">
        Datas inclusivas no fuso {snapshot.timezone}. Coleta em {stamp(snapshot.collectedAt)} · Meta
        Marketing API {snapshot.apiVersion}.
      </p>
      {snapshot.empty && (
        <p className="notice">
          A Meta não retornou dados para este período. Ausência de dados não confirma gasto ou
          resultado zero.
        </p>
      )}
      <dl className="ads-metrics">
        {snapshot.metrics.map((metric) => (
          <div key={metric.key}>
            <dt>{metric.label}</dt>
            <dd>{valueLabel(metric, snapshot.currency)}</dd>
          </div>
        ))}
      </dl>
      <details>
        <summary>Ações atribuídas e origem</summary>
        <p>
          Consulta com atribuição de 7 dias após clique e 1 dia após visualização, reportada pela
          data da impressão. Esses valores podem diferir do Gerenciador de Anúncios quando a
          configuração do relatório é outra.
        </p>
        {snapshot.actions.length ? (
          <dl className="ads-actions">
            {snapshot.actions.map((action) => (
              <div key={action.type}>
                <dt>{action.type}</dt>
                <dd>
                  {action.value === null ? 'Indisponível' : action.value.toLocaleString('pt-BR')}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>
            {snapshot.actionsStatus === 'unavailable'
              ? 'Ações não disponibilizadas pela Meta nesta leitura.'
              : 'A Meta não informou tipos de ação nesta leitura.'}
          </p>
        )}
        <p className="fine-print">
          Tipos de ação podem se sobrepor. Não são somados como um total de resultados, pessoas ou
          vendas. Esta tela não calcula ROI.
        </p>
      </details>
    </article>
  );
}

export function AdsWorkspace({ campaignId, data }: { campaignId: string; data: State }) {
  const [workspace, setWorkspace] = useState<AdsWorkspaceData>();
  const [accountId, setAccountId] = useState('');
  const [adsCampaignId, setAdsCampaignId] = useState('');
  const [scope, setScope] = useState<'campaign' | 'account'>('campaign');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  async function load() {
    const response = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/ads`);
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível consultar anúncios.');
    setWorkspace(result);
  }
  useEffect(() => {
    let active = true;
    setWorkspace(undefined);
    setAccountId('');
    setAdsCampaignId('');
    setError('');
    setNotice('');
    fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/ads`)
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Não foi possível consultar anúncios.');
        if (active) setWorkspace(result);
      })
      .catch((reason) => {
        if (active) setError(reason.message);
      });
    return () => {
      active = false;
    };
  }, [campaignId]);
  async function action(path: string, body: unknown, message: string) {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Não foi possível concluir a consulta.');
      await load();
      setNotice(message);
    } catch (reason) {
      setError((reason as Error).message);
      await load().catch(() => {});
    } finally {
      setBusy(false);
    }
  }
  const canRead =
    data.permissions.edit && !!workspace && ['ready', 'needs_discovery'].includes(workspace.access);
  const selectedAccount = workspace?.accounts.find((account) => account.id === accountId);
  const accountSnapshots =
    workspace?.accountSnapshots.filter(
      (snapshot) => !accountId || snapshot.accountId === accountId,
    ) ?? [];
  const campaigns =
    workspace?.campaigns.filter((campaign) => campaign.accountId === accountId) ?? [];
  const current = workspace?.links.find(
    (link) => link.link.accountId === accountId && link.link.adsCampaignId === adsCampaignId,
  );
  function collect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    void action(
      '/api/meta/ads/insights',
      {
        accountId,
        adsCampaignId: scope === 'campaign' ? adsCampaignId : null,
        since: String(form.get('since')),
        until: String(form.get('until')),
      },
      'Leitura de anúncios salva com período e atribuição.',
    );
  }
  return (
    <section className="ads-workspace panel" aria-labelledby="ads-heading">
      <div className="ads-section-heading">
        <div>
          <h2 id="ads-heading">Meta Ads · leitura</h2>
          <p>
            Associe uma campanha existente de anúncios e consulte seus resultados. As consultas não
            criam anúncios nem alteram orçamento ou veiculação.
          </p>
        </div>
      </div>
      {error && (
        <p role="alert" className="notice error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="notice">
          {notice}
        </p>
      )}
      {!workspace ? (
        !error && <p>Carregando anúncios…</p>
      ) : (
        <>
          <p className="notice">{workspace.message}</p>
          {workspace.lastError && (
            <p className="fine-print">Última tentativa: {workspace.lastError}</p>
          )}
          {canRead && (
            <div className="ads-controls">
              <button
                disabled={busy}
                onClick={() =>
                  void action('/api/meta/ads/accounts/sync', {}, 'Contas autorizadas conferidas.')
                }
              >
                {busy ? 'Consultando…' : 'Ler contas de anúncios'}
              </button>
              <label className="field">
                <span>Conta de anúncios autorizada</span>
                <select
                  value={accountId}
                  disabled={busy}
                  onChange={(event) => {
                    setAccountId(event.target.value);
                    setAdsCampaignId('');
                    setNotice('');
                    setError('');
                  }}
                >
                  <option value="">Selecione uma conta</option>
                  {workspace.accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} · {account.currency} · {account.id}
                    </option>
                  ))}
                </select>
              </label>
              {selectedAccount && (
                <>
                  <p className="fine-print">
                    Moeda {selectedAccount.currency} · fuso {selectedAccount.timezone}. O total da
                    conta inclui todas as campanhas e não é atribuído à campanha editorial.
                  </p>
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(
                        '/api/meta/ads/campaigns/sync',
                        { accountId },
                        'Campanhas da conta conferidas.',
                      )
                    }
                  >
                    Ler campanhas da conta
                  </button>
                  <label className="field">
                    <span>Campanha existente na Meta</span>
                    <select
                      value={adsCampaignId}
                      disabled={busy}
                      onChange={(event) => setAdsCampaignId(event.target.value)}
                    >
                      <option value="">Selecione uma campanha de anúncios</option>
                      {campaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name} · {campaign.status} · {campaign.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  {adsCampaignId && (
                    <button
                      className="primary"
                      disabled={busy || !!current}
                      onClick={() =>
                        void action(
                          '/api/ads/links',
                          { campaignId, accountId, adsCampaignId },
                          'Campanha de anúncios vinculada a esta campanha editorial.',
                        )
                      }
                    >
                      {current ? 'Vínculo já registrado' : 'Vincular à campanha editorial'}
                    </button>
                  )}
                  <form className="ads-collection" onSubmit={collect}>
                    <label className="field">
                      <span>Escopo da leitura de anúncios</span>
                      <select
                        value={scope}
                        disabled={busy}
                        onChange={(event) => setScope(event.target.value as 'campaign' | 'account')}
                      >
                        <option value="campaign">Campanha de anúncios selecionada</option>
                        <option value="account">Conta inteira · sem atribuição editorial</option>
                      </select>
                    </label>
                    <label className="field">
                      <span>Início do período de anúncios</span>
                      <input
                        name="since"
                        type="date"
                        defaultValue={weekAgo}
                        required
                        disabled={busy}
                      />
                    </label>
                    <label className="field">
                      <span>Fim do período de anúncios</span>
                      <input
                        name="until"
                        type="date"
                        defaultValue={yesterday}
                        required
                        disabled={busy}
                      />
                    </label>
                    <p className="fine-print">
                      Até 31 dias, incluindo início e fim, no fuso da conta. Atribuição da consulta:
                      7 dias após clique e 1 dia após visualização; data da impressão.
                    </p>
                    <button
                      className="primary"
                      disabled={busy || (scope === 'campaign' && (!adsCampaignId || !current))}
                    >
                      Consultar resultados de anúncios
                    </button>
                    {scope === 'campaign' && adsCampaignId && !current && (
                      <small>Registre o vínculo acima para acompanhar esta campanha aqui.</small>
                    )}
                  </form>
                </>
              )}
            </div>
          )}
          {!data.permissions.edit && (
            <p className="fine-print">
              Seu papel permite consultar os resultados compartilhados. Um editor ou administrador
              pode atualizar as leituras.
            </p>
          )}
          <div className="ads-linked">
            <h3>Campanhas de anúncios vinculadas</h3>
            {!workspace.links.length && (
              <p>
                Nenhuma campanha de anúncios vinculada. Os planos manuais de impulsionamento
                permanecem separados.
              </p>
            )}
            {workspace.links.map((entry) => (
              <article className="ads-campaign" key={entry.link.id}>
                <h4>{entry.adsCampaign?.name || `Campanha Meta ${entry.link.adsCampaignId}`}</h4>
                {entry.account && (
                  <p>
                    {entry.account.name} · {entry.account.currency} · {entry.adsCampaign?.status} ·
                    ID {entry.link.adsCampaignId}
                  </p>
                )}
                {entry.unavailable ? (
                  <p className="notice">
                    A conta não está atualmente concedida por uma conexão ativa da equipe. Dados e
                    leituras Meta ficam ocultos até nova conferência de acesso.
                  </p>
                ) : !entry.snapshots.length ? (
                  <p>
                    Ainda não há leitura desta campanha. Selecione a conta e a campanha acima para
                    consultar.
                  </p>
                ) : (
                  <>
                    <Reading snapshot={entry.snapshots[0]} accountName={entry.account?.name} />
                    {entry.snapshots.length > 1 && (
                      <details>
                        <summary>Histórico de leituras ({entry.snapshots.length - 1})</summary>
                        {entry.snapshots.slice(1).map((snapshot) => (
                          <Reading
                            key={snapshot.id}
                            snapshot={snapshot}
                            accountName={entry.account?.name}
                          />
                        ))}
                      </details>
                    )}
                  </>
                )}
              </article>
            ))}
          </div>
          {!!accountSnapshots.length && (
            <details className="ads-account-history">
              <summary>
                {selectedAccount ? 'Leituras da conta selecionada' : 'Leituras de contas inteiras'}{' '}
                · sem atribuição à campanha ({accountSnapshots.length})
              </summary>
              {accountSnapshots.map((snapshot) => (
                <Reading
                  key={snapshot.id}
                  snapshot={snapshot}
                  accountName={
                    workspace.accounts.find((account) => account.id === snapshot.accountId)?.name
                  }
                />
              ))}
            </details>
          )}
          {selectedAccount && !accountSnapshots.length && (
            <p className="fine-print ads-account-empty">
              Ainda não há leituras salvas da conta inteira {selectedAccount.name} ·{' '}
              {selectedAccount.id}.
            </p>
          )}
          <p className="fine-print">
            Moedas, alcance e períodos sobrepostos não são somados. Resultados atribuídos pela Meta
            não comprovam vendas ou retorno financeiro do FLG.
          </p>
        </>
      )}
    </section>
  );
}
